import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { TranslateModule } from '@ngx-translate/core';
import { FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastService } from 'src/app/base/services/toast.service';
import { ErrorType } from 'src/app/core/routing/error-types';
import { AssetEditTabGroupComponent } from "../../../shared/asset-edit-tab-group/asset-edit-tab-group.component";
import { AssetUploadEditorFormType } from '../../../shared/asset-upload-editor/asset-upload-editor-form-type';
import { ProductAssetDTOStargate } from 'src/app/base/models/models.swagger.generated';
import { MultiUploadUploaderComponent } from "../../../shared/multi-upload-uploader/multi-upload-uploader.component";

@Component({
    selector: 'app-product-assets-multi-upload',
    templateUrl: './product-assets-multi-upload.component.html',
    styleUrl: './product-assets-multi-upload.component.scss',
    imports: [DialogModule, TranslateModule, CommonModule, AssetEditTabGroupComponent, MultiUploadUploaderComponent]
})
export class ProductAssetsMultiUploadComponent {

  @Output()
  public OnClose: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('multiAssetUploadDialog')
  public dialog: DialogComponent;

  @ViewChild('multiUploadUploaderComponent')
  public multiUploadUploaderComponent: MultiUploadUploaderComponent;

  @ViewChild("assetEditTabGroupComponent")
  public assetEditTabGroupComponent: AssetEditTabGroupComponent;

  public productAssetUploadEditorForms: FormGroup<AssetUploadEditorFormType>[] = [];
  public selectedProductAssetUploadEditorForm: FormGroup<AssetUploadEditorFormType> = null;

  public productNumbers: string[] = [];
  public isUploading: boolean = false;

  public get canSave() {
    return this.productAssetUploadEditorForms.length && (this.productAssetUploadEditorForms?.every((form) => form.valid) ?? false) && !this.assetEditTabGroupComponent?.assetImageEditorComponent?.hasPendingChanges()
  }

  private isFullScreen: boolean = false;

  constructor(
    private toastService: ToastService
  ) { }

  public onCancel() {
    this.dialog.hide();
    this.resetDialog();
  }

  public onOpen(args: any): void {
    //Preventing the default dialog focus
    args.preventFocus = true;
  }

  public async onSave() {
    if (!this.canSave) {
      this.productAssetUploadEditorForms.forEach((form) => {
        form.markAllAsTouched();
        form.markAsDirty();
      });
      if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
        this.toastService.showWarning('i18n.domain.assets.asset-storage.edit-asset-storage.form-invalid.title',
          'i18n.domain.assets.asset-storage.edit-asset-storage.form-invalid.message', null);
      }
      return;
    }
    //Check for duplicate IsKey values
    // group the form raw values by type and check there are multiple files has isKey = true for the same type
    const groupedForms = this.productAssetUploadEditorForms.reduce((acc, form) => {
      const type = form.controls.type.value;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(form.getRawValue());
      return acc;
    }, {} as Record<string, any[]>);
    const hasDuplicateKey = Object.values(groupedForms).some((forms) => {
      return forms.filter((form) => form.isKey).length > 1;
    });
    if (hasDuplicateKey) {
      this.toastService.showWarning('i18n.domain.assets.product-assets.edit-asset.messages.multi-upload.duplicate-key-title',
        'i18n.domain.assets.product-assets.edit-asset.messages.multi-upload.duplicate-key-message', null);
      return;
    }

    this.isUploading = true;

    //Get editor data
    var extension = this.selectedProductAssetUploadEditorForm.controls.fileExtension.value?.toLowerCase();
    if ((extension == "jpg" || extension == "jpeg" || extension == "png") &&
      this.assetEditTabGroupComponent.assetImageEditorComponent?.imageEditorObj) {
      await this.assetEditTabGroupComponent.getImageDataFromCanvas();
    }

    //The uploader handles the upload itself
    this.multiUploadUploaderComponent.upload();
  }

  public onUploadSuccessful() {
    this.OnClose.emit();
    this.dialog.hide();
    this.resetDialog();
  }

  public assignAssets(selectedProducts: ProductAssetDTOStargate[]) {
    this.productNumbers = selectedProducts.map((p) => p.sKU);
    this.dialog.show(false);
  }

  public fileForEditSelected(fileId: string) {
    this.selectedProductAssetUploadEditorForm = null;
    if (fileId != null) {
      //Exit possible fullscreen mode
      if (this.isFullScreen) {
        this.dialog.show(false);
      }
      setTimeout(() => {
        this.selectedProductAssetUploadEditorForm = this.productAssetUploadEditorForms.find((form) => form.controls.intermediateFileInfo.value.id === fileId) ?? null;
      }, 50);
    }
  }

  public setScreenSize(isFullScreen: boolean) {
    this.isFullScreen = isFullScreen;
    isFullScreen ? this.dialog.show(true) : this.dialog.show(false);
  }

  protected getFormFromFileId(fileId: string): FormGroup<AssetUploadEditorFormType> {
    return this.productAssetUploadEditorForms.find((form) => form.controls.intermediateFileInfo.value.id === fileId) ?? null;
  }

  private resetDialog() {
    this.isFullScreen = false;
    this.selectedProductAssetUploadEditorForm = null;
    this.isUploading = false;
    this.productAssetUploadEditorForms = [];
    this.productNumbers = [];
    this.multiUploadUploaderComponent?.clearUploader();
  }
}
